import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Play, Plus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "@/client/lib/api";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Textarea } from "../../components/ui/textarea";

interface WorkflowSummary {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

interface StepEvent {
  stepIndex: number;
  tool: string;
  status: "running" | "done";
  output?: string;
}

function useWorkflows() {
  return useQuery({
    queryKey: ["workflows"],
    queryFn: () => api.get<{ workflows: WorkflowSummary[] }>("/api/workflows"),
  });
}

function WorkflowForm({ onClose }: { onClose: () => void }): React.ReactElement {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [definitionJson, setDefinitionJson] = useState(
    JSON.stringify(
      {
        id: "new",
        name: "My workflow",
        description: "",
        steps: [{ tool: "get_expiring_records", args: { days: 60 } }],
        dryRun: true,
      },
      null,
      2,
    ),
  );
  const [jsonError, setJsonError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post("/api/workflows", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
      onClose();
    },
  });

  function handleSubmit(): void {
    try {
      const parsed = JSON.parse(definitionJson) as unknown;
      setJsonError(null);
      mutation.mutate({ name, description, definitionJson: parsed });
    } catch {
      setJsonError("Invalid JSON — please fix the workflow definition.");
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="wf-name" className="text-xs font-medium text-muted-foreground block mb-1">
          Name
        </label>
        <input
          id="wf-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="wf-name"
        />
      </div>
      <div>
        <label htmlFor="wf-desc" className="text-xs font-medium text-muted-foreground block mb-1">
          Description
        </label>
        <input
          id="wf-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="wf-desc"
        />
      </div>
      <div>
        <label htmlFor="wf-json" className="text-xs font-medium text-muted-foreground block mb-1">
          Workflow Definition (JSON)
        </label>
        <Textarea
          id="wf-json"
          value={definitionJson}
          onChange={(e) => setDefinitionJson(e.target.value)}
          className="font-mono text-xs h-48"
          data-testid="wf-json"
        />
        {jsonError && <p className="text-xs text-destructive mt-1">{jsonError}</p>}
      </div>
      <div className="flex gap-2 pt-2">
        <Button onClick={handleSubmit} disabled={!name.trim() || mutation.isPending} data-testid="wf-save">
          Save
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
      {mutation.isError && <p className="text-xs text-destructive">{(mutation.error as Error).message}</p>}
    </div>
  );
}

function RunPanel({ workflowId, onClose }: { workflowId: string; onClose: () => void }): React.ReactElement {
  const [events, setEvents] = useState<StepEvent[]>([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  async function startRun(): Promise<void> {
    setEvents([]);
    setError(null);
    setRunning(true);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await api.streamPost(
        `/api/workflows/${workflowId}/run`,
        { dryRun: false },
        (raw) => {
          const data = raw as unknown as { stepIndex?: number; tool?: string; status?: string; output?: string };
          if (data.stepIndex !== undefined && data.tool) {
            setEvents((prev) => {
              const idx = prev.findIndex((e) => e.stepIndex === data.stepIndex && e.tool === data.tool);
              const next: StepEvent = {
                stepIndex: data.stepIndex ?? 0,
                tool: data.tool ?? "",
                status: (data.status as "running" | "done") ?? "running",
                output: data.output,
              };
              if (idx >= 0) {
                const copy = [...prev];
                copy[idx] = next;
                return copy;
              }
              return [...prev, next];
            });
          }
        },
        controller.signal,
      );
    } catch (err) {
      if (err instanceof Error && err.name !== "AbortError") {
        setError(err.message);
      }
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Button size="sm" onClick={startRun} disabled={running} data-testid="run-start">
          <Play className="h-3.5 w-3.5" /> {running ? "Running…" : "Run"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="space-y-1 max-h-60 overflow-y-auto">
        {events.map((ev) => (
          <div key={`${ev.stepIndex}-${ev.status}`} className="text-xs rounded border bg-muted px-2 py-1 font-mono">
            <span className="text-muted-foreground">step {ev.stepIndex}: </span>
            <span className="font-medium">{ev.tool}</span>
            <span className={ev.status === "done" ? "text-green-600 ml-2" : "text-yellow-600 ml-2"}>[{ev.status}]</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function WorkflowsPage(): React.ReactElement {
  const { data, isLoading } = useWorkflows();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [runningId, setRunningId] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/workflows/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["workflows"] }),
  });

  const workflows = data?.workflows ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Workflows</h2>
          <p className="text-sm text-muted-foreground">
            Automate multi-step operations across insurance records and external services.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowForm(true)} data-testid="add-workflow">
          <Plus className="h-3.5 w-3.5" /> New Workflow
        </Button>
      </div>

      {runningId && (
        <div className="rounded-md border p-3">
          <p className="text-xs font-semibold mb-2">Run output</p>
          <RunPanel workflowId={runningId} onClose={() => setRunningId(null)} />
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="w-32">Created</TableHead>
            <TableHead className="w-28" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                Loading…
              </TableCell>
            </TableRow>
          ) : workflows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                No workflows yet. Create one to get started.
              </TableCell>
            </TableRow>
          ) : (
            workflows.map((wf) => (
              <TableRow key={wf.id}>
                <TableCell className="font-medium">{wf.name}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{wf.description || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {new Date(wf.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setRunningId(wf.id)} data-testid={`run-${wf.id}`}>
                      <Play className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteMutation.mutate(wf.id)}
                      disabled={deleteMutation.isPending}
                      data-testid={`delete-${wf.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Workflow</DialogTitle>
          </DialogHeader>
          <WorkflowForm onClose={() => setShowForm(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
